/**
 * The issue form fields a company can show/hide/require. Mirrors
 * `IssueFormField` in the backend's `issues/consts_fields.py` — values are the
 * Issue model field names.
 *
 * `CATEGORY` is in here because the form renders it and the validators name
 * it, but the backend does not report it as configurable: the category is what
 * selects a configuration, so it is always shown and always required. See
 * `ALWAYS_ON_ISSUE_FIELDS`.
 */
export const IssueFormField = {
  TITLE: 'title',
  CATEGORY: 'category',
  SUBCATEGORY: 'subcategory',
  SEVERITY: 'severity',
  RISK_ASPECT: 'risk_aspect',
  PROCESS_CHOICE: 'process_choice',
  RESPONSIBLE_USER: 'responsible_user',
  CASE_HANDLER: 'case_handler',
  TAGS: 'tags',
  TIME_OF_ISSUE: 'time_of_issue',

  LOCATION_PLACENAME: 'location_placename',
  LOCATION_DESCRIPTION: 'location_description',
  VEHICLE_PLATE_NUMBER: 'vehicle_plate_number',

  ISSUE_DESCRIPTION: 'issue_description',
  DAMAGE_DESCRIPTION: 'damage_description',
  REASON: 'reason',

  IMMEDIATE_ACTION: 'immediate_action',
  ACTION: 'action',
  SOLUTION_SUGGESTION: 'solution_suggestion',

  COST_ESTIMATION: 'cost_estimation',
  ATTACHMENTS: 'attachments',
} as const

export type IssueFormFieldValue =
  (typeof IssueFormField)[keyof typeof IssueFormField]

/** Fields the company cannot configure away. Visible and required, always. */
export const ALWAYS_ON_ISSUE_FIELDS: readonly IssueFormFieldValue[] = [
  IssueFormField.CATEGORY,
]

export const IssueFormSection = {
  META: 'META',
  LOCATION: 'LOCATION',
  EVENT: 'EVENT',
  RESOLUTION: 'RESOLUTION',
  COST: 'COST',
} as const

export type IssueFormSectionValue =
  (typeof IssueFormSection)[keyof typeof IssueFormSection]

/** One field as the company has it configured, plus its code-level limits. */
export interface IssueFieldConfig {
  field: IssueFormFieldValue
  section: IssueFormSectionValue
  order: number
  labelOverride: string
  visibleOnCreate: boolean
  requiredOnCreate: boolean
  visibleOnEdit: boolean
  requiredOnEdit: boolean
  canHide: boolean
  canRequire: boolean
  /** True when a per-category override supplied the four flags. */
  overridden: boolean
}

export interface IssueFieldConfigsQuery {
  issueFieldConfigs: IssueFieldConfig[]
}

/** Which half of the flow a field is being rendered for. */
export type IssueFormMode = 'create' | 'edit'

export interface SetIssueFieldConfigsInput {
  field: IssueFormFieldValue
  visibleOnCreate?: boolean
  requiredOnCreate?: boolean
  visibleOnEdit?: boolean
  requiredOnEdit?: boolean
  order?: number
  labelOverride?: string
}

/** A category override row — order and label stay company-wide. */
export interface SetIssueCategoryFieldConfigsInput {
  field: IssueFormFieldValue
  visibleOnCreate?: boolean
  requiredOnCreate?: boolean
  visibleOnEdit?: boolean
  requiredOnEdit?: boolean
}

/** One row of the settings category picker. */
export interface IssueFormFieldCategory {
  id: string
  name: string
  hasFieldConfigs: boolean
}

export interface IssueFormFieldCategoriesQuery {
  allIssueCategories: { edges: { node: IssueFormFieldCategory }[] }
}

/** One company-defined relation, resolved for the category in play. */
export interface IssueRelationConfig {
  relation: string
  name: string
  visibleOnCreate: boolean
  requiredOnCreate: boolean
  visibleOnEdit: boolean
  requiredOnEdit: boolean
  overridden: boolean
}

export interface IssueRelationConfigsQuery {
  issueRelationConfigs: IssueRelationConfig[]
}

/** A relation override row — the same four flags, keyed by relation id. */
export interface SetIssueRelationCategoryConfigsInput {
  relation: string
  visibleOnCreate?: boolean
  requiredOnCreate?: boolean
  visibleOnEdit?: boolean
  requiredOnEdit?: boolean
}
